/**
 * UnlockContentPreviewCarousel
 *
 * Animated carousel of blurred preview images for locked content.
 * Cycles through media.previews[] at 1500ms intervals with a cross-fade
 * transition, matching Flutter's UnlockContentWidget behaviour.
 *
 * Flutter: lib/presentation/screens/content/custom_widgets/scrollable/unlock_content_widget.dart
 */
import React, { useState, useEffect, useRef } from 'react';
import { CachedImage, preloadImage } from '@/presentation/components/common/CachedImage';
import { env } from '@/config/env';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

const PREVIEW_INTERVAL_MS = 1500;
const BLUR_PX = 8;

interface UnlockContentPreviewCarouselProps {
  media: UserMediaEntity;
  style?: React.CSSProperties;
  className?: string;
}

export const UnlockContentPreviewCarousel: React.FC<UnlockContentPreviewCarouselProps> = ({
  media,
  style,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isPausedRef = useRef(false);

  const previews = media.previews ?? [];

  const buildPreviewUrl = (preview: string): string => {
    if (!preview) return '';
    if (preview.startsWith('http')) return preview;
    return `${env.CDN_NAME}${preview}`;
  };

  const startInterval = () => {
    if (previews.length <= 1) return;
    intervalRef.current = setInterval(() => {
      if (!isPausedRef.current) {
        setCurrentIndex((prev) => (prev + 1) % previews.length);
      }
    }, PREVIEW_INTERVAL_MS);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Pre-cache the next image while the current one is displayed
  useEffect(() => {
    if (previews.length <= 1) return;
    const nextIndex = (currentIndex + 1) % previews.length;
    const nextUrl = buildPreviewUrl(previews[nextIndex]);
    if (nextUrl) {
      preloadImage(nextUrl);
    }
  }, [currentIndex, previews]);

  useEffect(() => {
    if (previews.length === 0) return;

    startInterval();

    // Pause animation when not visible (IntersectionObserver)
    const observer = new IntersectionObserver(
      ([entry]) => {
        isPausedRef.current = !entry.isIntersecting || entry.intersectionRatio < 0.5;
      },
      { threshold: 0.5 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      stopInterval();
      observer.disconnect();
    };
  }, [previews.length]);

  if (previews.length === 0) {
    return <div ref={containerRef} {...qaAttr({ id: 'unlock-content-preview' })} className={className} style={style} />;
  }

  const currentUrl = buildPreviewUrl(previews[currentIndex]);

  return (
    <div
      ref={containerRef}
      {...qaAttr({ id: 'unlock-content-preview' })}
      className={`overflow-hidden ${className}`}
      style={{ position: 'relative', ...style }}
    >
      <CachedImage
        src={currentUrl}
        alt="Content preview"
        useOldImageOnUrlChange={true}
        fadeInDuration={400}
        fadeOutDuration={400}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: `blur(${BLUR_PX}px)`,
          transform: 'scale(1.1)', // slight scale to hide blur edges
        }}
      />
    </div>
  );
};
